import { CheckBoxSwitcher, FieldLabel, Tooltip } from '@systemeio/ui'
import { DateSpecificAvailabilitySlots } from 'modules/calendar/availability/components/date-specific-availability-slots'
import { WeekdaysAvailabilitySlots } from 'modules/calendar/availability/components/weekdays-availability-slots'
import { useDraftAvailabilitySlots } from 'modules/calendar/availability/hooks/use-draft-availability-slots'
import { AvailabilitySlot } from 'modules/calendar/availability/types/availability-interface'
import { groupAvailabilities } from 'modules/calendar/availability/utils'
import { useEffect } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface EventAvailabilityProps {
  useGlobalAvailability: boolean
  onSelectGlobalAvailability: (value: boolean) => void
  defaultSlots?: AvailabilitySlot[]
  globalSlots?: AvailabilitySlot[]
  onChange: (updatedSlots: AvailabilitySlot[]) => void
  checkboxError?: string
  error?: string
  isLoading?: boolean
  minDate?: Date
  maxDate?: Date
}

export function EventAvailability({
  useGlobalAvailability,
  onSelectGlobalAvailability,
  defaultSlots,
  globalSlots = [],
  onChange,
  checkboxError,
  error,
  isLoading,
  minDate,
  maxDate,
}: EventAvailabilityProps) {
  const { t } = useLocoTranslation()
  const {
    draftAvailabilitySlots,
    weekdaysSlots,
    dateSpecificSlots,
    addSlot,
    removeSlots,
    updateSlot,
  } = useDraftAvailabilitySlots(defaultSlots)

  const globalAvailability = groupAvailabilities(globalSlots)

  useEffect(() => {
    onChange(draftAvailabilitySlots)
  }, [draftAvailabilitySlots])

  return (
    <div>
      <FieldLabel labelClassName="mb-2" label={t('dashboard.calendar.available_hours')} />
      {checkboxError ? (
        <Tooltip label={checkboxError} mode="hover">
          <CheckBoxSwitcher
            label={t('dashboard.calendar.use_global_availability')}
            selected={useGlobalAvailability}
            onChange={onSelectGlobalAvailability}
            disabled={!globalSlots?.length}
          />
        </Tooltip>
      ) : (
        <CheckBoxSwitcher
          label={t('dashboard.calendar.use_global_availability')}
          selected={useGlobalAvailability}
          onChange={onSelectGlobalAvailability}
          disabled={!globalSlots?.length}
        />
      )}

      <div className="mt-8 flex w-full flex-col xl:flex-row">
        <div className="mb-10 flex-1 md:pr-10">
          <WeekdaysAvailabilitySlots
            slots={useGlobalAvailability ? globalAvailability.weekdaysSlots : weekdaysSlots}
            addSlot={addSlot}
            removeSlots={removeSlots}
            updateSlot={updateSlot}
            disabled={useGlobalAvailability}
            isLoading={isLoading}
            titleClassName="text-lg text-text-primary"
          />
        </div>
        <div className="border-text-secondary/30 flex-1 xl:md:border-l xl:md:pl-10">
          <DateSpecificAvailabilitySlots
            slots={useGlobalAvailability ? globalAvailability.dateSpecificSlots : dateSpecificSlots}
            addSlot={addSlot}
            removeSlots={removeSlots}
            updateSlot={updateSlot}
            disabled={useGlobalAvailability}
            isLoading={isLoading}
            minDate={minDate}
            maxDate={maxDate}
            titleClassName="text-lg text-text-primary"
          />
        </div>
      </div>
      <FieldErrorAndDescription error={error} />
    </div>
  )
}
